import { useMemo } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import { ELIGIBLE_AD_GROUP_IDS } from 'constants/salesCtrRankingAdGroups'
import Places from 'constants/places'
import { TOP_N, HOTEL_OFFER_TYPES, hasTopOffersChanged } from 'hooks/salesRankingUtils'

const BALI_DESTINATION_ID = Places.Bali.id

interface UseSalesAndCtrRankingResult {
  experimentVariant: string | undefined
  rankingComparisonPending: boolean
}

function useSalesAndCtrRanking(destinationId: string | undefined): UseSalesAndCtrRankingResult {
  const query = useQueryParams()
  const utmAdgroup = query.get('utm_adgroup')

  const isEligibleAdgroup = !!utmAdgroup && ELIGIBLE_AD_GROUP_IDS.has(utmAdgroup)
  const isBali = destinationId === BALI_DESTINATION_ID
  const shouldCompare = isEligibleAdgroup && !!destinationId && !isBali

  const baseFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!shouldCompare) return undefined
    return {
      destinationId,
      offerTypes: HOTEL_OFFER_TYPES,
      limit: TOP_N,
    }
  }, [shouldCompare, destinationId])

  const filtersWithRollout = useMemo((): App.OfferListFilters | undefined => {
    if (!baseFilters) return undefined
    return {
      ...baseFilters,
      rollout: ['salesCtrRanking'],
    }
  }, [baseFilters])

  const withRolloutList = useOfferList(filtersWithRollout, { disabled: !shouldCompare })
  const withoutRolloutList = useOfferList(baseFilters, { disabled: !shouldCompare })

  const rankingComparisonPending = shouldCompare && (withRolloutList.fetching || withoutRolloutList.fetching)

  const rankingsDiffer = useMemo(() => {
    if (!shouldCompare || rankingComparisonPending) return false

    return hasTopOffersChanged(withRolloutList.offerIds, withoutRolloutList.offerIds)
  }, [shouldCompare, rankingComparisonPending, withRolloutList.offerIds, withoutRolloutList.offerIds])

  const canActivate = isEligibleAdgroup && !rankingComparisonPending && rankingsDiffer

  const experimentVariant = useOptimizelyExperiment(OptimizelyExperiments.searchSalesCtrPaidTrafficRanking, canActivate)

  return { experimentVariant, rankingComparisonPending }
}

export default useSalesAndCtrRanking
